import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { 
  Info, User, Target, Zap, Brain, TrendingUp, AlertCircle, Check,
  RefreshCw, Star, ChevronRight, Activity, Layers
} from 'lucide-react';
import DevelopmentTimeline from '@/components/DevelopmentTimeline';
import { trackEvent } from '@/lib/telemetry';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score thresholds for level determination
const SCORE_THRESHOLDS = {
  proficient: 8.1,
  developing: 6.1,
};

// Radar Chart Component with Interactive Tooltips
const RadarChart = ({ data, size = 300 }) => {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, label: '', value: 0 });
  const [pointsData, setPointsData] = useState([]);
  
  // Get level from score
  const getLevel = (value) => {
    if (value >= SCORE_THRESHOLDS.proficient) return { text: 'Proficient', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (value >= SCORE_THRESHOLDS.developing) return { text: 'Developing', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { text: 'Focus', color: 'text-red-400', bg: 'bg-red-500/20' };
  };
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size / 2) - 50; // Increased margin for labels
    
    ctx.clearRect(0, 0, size, size);
    
    const labels = Object.keys(data);
    const values = Object.values(data);
    const numPoints = labels.length;
    const angleStep = (Math.PI * 2) / numPoints;
    
    // Store points for hover detection
    const points = [];
    
    // Draw grid circles
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 5; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / 5, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Draw grid lines
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.stroke();
    }
    
    // Draw data polygon
    ctx.beginPath();
    ctx.fillStyle = 'rgba(218, 174, 62, 0.3)';
    ctx.strokeStyle = '#daae3e';
    ctx.lineWidth = 2;
    
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = Math.max(0, Math.min(10, values[i])) / 10;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      points.push({ x, y, label: labels[i], value: values[i] });
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw points
    ctx.fillStyle = '#daae3e';
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = Math.max(0, Math.min(10, values[i])) / 10;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);
      
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Draw labels - properly handle multi-word labels
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.font = '11px system-ui';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 35;
      let x = centerX + labelRadius * Math.cos(angle);
      let y = centerY + labelRadius * Math.sin(angle);
      
      // Adjust text alignment based on position
      if (Math.cos(angle) > 0.3) {
        ctx.textAlign = 'left';
        x += 5;
      } else if (Math.cos(angle) < -0.3) {
        ctx.textAlign = 'right';
        x -= 5;
      } else {
        ctx.textAlign = 'center';
      }
      
      const label = labels[i];
      // Abbreviate long labels
      const displayLabel = label
        .replace('Decision Making', 'Decision')
        .replace('Game Awareness', 'Awareness')
        .replace('Speed & Agility', 'Speed/Agility')
        .replace('Balance & Coord', 'Balance/Coord');
      
      ctx.fillText(displayLabel, x, y);
    }
    
    setPointsData(points);
  }, [data, size]);
  
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !pointsData.length) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    let foundPoint = null;
    for (const point of pointsData) {
      const distance = Math.sqrt(Math.pow(mouseX - point.x, 2) + Math.pow(mouseY - point.y, 2));
      if (distance < 20) {
        foundPoint = point;
        break;
      }
    }
    
    if (foundPoint) {
      setTooltip({
        visible: true,
        x: foundPoint.x,
        y: foundPoint.y - 40,
        label: foundPoint.label,
        value: foundPoint.value
      });
    } else {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };
  
  return (
    <div className="relative">
      <canvas 
        ref={canvasRef} 
        width={size} 
        height={size}
        className="mx-auto cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      {/* Tooltip */}
      {tooltip.visible && (
        <div 
          className="absolute pointer-events-none z-10 transform -translate-x-1/2"
          style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
        >
          <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-center min-w-[120px]">
            <p className="text-xs text-muted-foreground mb-1">{tooltip.label}</p>
            <p className="text-lg font-bold text-primary">{tooltip.value?.toFixed(1)}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded ${getLevel(tooltip.value).bg} ${getLevel(tooltip.value).color}`}>
              {getLevel(tooltip.value).text}
            </span>
          </div>
          <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1">
            <div className="w-2 h-2 bg-card border-r border-b border-border rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const DevelopmentProfile = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  // V1: Profile context selector — Unified (default) or filtered to a single
  // coach perspective (Club / Soccer / Performance). Filters the radar and
  // scores client-side; does NOT change the cached unified profile.
  const [viewContext, setViewContext] = useState('unified');
  // V1: Performance Coach invite modal
  const [showPerfInvite, setShowPerfInvite] = useState(false);
  const [perfInviteEmail, setPerfInviteEmail] = useState('');
  const [perfInviteSubmitting, setPerfInviteSubmitting] = useState(false);
  const [perfInviteError, setPerfInviteError] = useState(null);

  const handleInvitePerformanceCoach = async (e) => {
    e.preventDefault();
    setPerfInviteSubmitting(true);
    setPerfInviteError(null);
    try {
      const res = await fetch(`${API}/parent/invite-performance-coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ athlete_id: athlete.id, coach_email: perfInviteEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invite failed');
      trackEvent('parent_invited_performance_coach', {
        target_id: athlete.id,
        target_type: 'athlete',
        metadata: { invited_email: perfInviteEmail.trim() },
        once: false,
      });
      setShowPerfInvite(false);
      setPerfInviteEmail('');
      // Refetch profile so Performance Coach card surfaces immediately
      fetchProfile();
    } catch (err) {
      setPerfInviteError(err.message);
    } finally {
      setPerfInviteSubmitting(false);
    }
  };

  useEffect(() => {
    if (athlete?.id) {
      fetchProfile();
      // Founding Beta telemetry — parent viewed development radar (aha-moment candidate)
      trackEvent('parent_viewed_radar', { target_id: athlete.id, target_type: 'athlete', once: true });
      trackEvent('parent_opened_player_profile', { target_id: athlete.id, target_type: 'athlete', once: true });
    }
  }, [athlete]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, timelineRes] = await Promise.all([
        fetch(`${API}/evaluations/athlete/${athlete.id}/profile`, { headers: getAuthHeaders() }),
        fetch(`${API}/evaluations/athlete/${athlete.id}/timeline`, { headers: getAuthHeaders() }).catch(() => null)
      ]);
      const data = await profileRes.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setProfile(data);
      }
      
      if (timelineRes) {
        const tData = await timelineRes.json();
        setTimeline(tData.history || []);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
      setTimelineLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="development-profile-loading">
        <RefreshCw className="animate-spin text-primary" size={32} />
        <span className="ml-3 text-muted-foreground">Loading development profile...</span>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6" data-testid="development-profile-empty">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Development Profile</h1>
          <p className="text-muted-foreground">{athlete?.name}'s compiled evaluation</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto text-muted-foreground mb-4" size={48} />
          <h3 className="text-lg font-medium mb-2">No Evaluation Data Yet</h3>
          <p className="text-muted-foreground text-sm">
            Your coach hasn't completed an evaluation yet. Once your coach evaluates your skills, 
            your development profile will appear here.
          </p>
        </div>
      </div>
    );
  }

  // Calculate axis scores for display
  const axisScores = profile.radar_data || {};

  // V1: detect which roles evaluated this athlete (read both new + legacy keys)
  const srcs = profile.evaluation_sources || {};
  const hasTeam = Boolean(srcs.team ?? srcs.team_coach);
  const hasSoccer = Boolean(srcs.private_soccer ?? srcs.private_coach);
  const hasPerformance = Boolean(srcs.private_performance);
  const availableContexts = [
    { id: 'unified', label: 'Unified', icon: Layers, visible: true },
    { id: 'team', label: 'Club Coach', icon: Brain, visible: hasTeam },
    { id: 'private_soccer', label: 'Soccer Coach', icon: Target, visible: hasSoccer },
    { id: 'private_performance', label: 'Performance Coach', icon: Activity, visible: hasPerformance },
  ].filter((c) => c.visible);

  // Physical-only radar axes (subset of the 8-axis radar)
  const PHYSICAL_AXES = ['Speed & Agility', 'Balance & Coordination'];
  // Per-skill physical (not aggregated): pull from compiled_skills if available
  const compiledSkills = profile.compiled_skills || [];
  const physicalSkillNames = ['Speed', 'Acceleration', 'Agility', 'Coordination', 'Balance', 'Explosiveness', 'Endurance'];
  const physicalRadar = {};
  for (const skill of compiledSkills) {
    if (physicalSkillNames.includes(skill.skill_name)) {
      physicalRadar[skill.skill_name] = skill.score;
    }
  }

  // Decide what to render in the radar based on viewContext
  const isPhysicalView = viewContext === 'private_performance';
  const radarData = isPhysicalView
    ? (Object.keys(physicalRadar).length ? physicalRadar : Object.fromEntries(
        Object.entries(axisScores).filter(([k]) => PHYSICAL_AXES.includes(k))
      ))
    : axisScores;
  const radarTitle = isPhysicalView ? 'Physical Radar' : 'Player Radar';

  // Filter axis-scores grid for the Performance view too
  const filteredAxisScores = isPhysicalView ? radarData : axisScores;

  return (
    <div className="space-y-6" data-testid="development-profile">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Development Profile</h1>
        <p className="text-muted-foreground">{athlete?.name}'s compiled evaluation</p>
      </div>

      {/* Info Card - How this profile is generated */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Info className="text-primary" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-lg mb-2">How this profile is generated</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This development profile is generated from evaluations provided by your coaches and analyzed 
              through the Meybra AI system. The platform compiles observations from training environments 
              and match situations to create a structured overview of the player's technical, physical, 
              and game intelligence development.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              This profile is designed to support long-term player development by highlighting strengths, 
              identifying areas for improvement, and tracking progress over time.
            </p>
          </div>
        </div>
      </div>

      {/* Profile Header Card */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <User size={32} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading">{profile.athlete_name || athlete?.name}</h2>
              <p className="text-primary font-medium">{profile.player_type || 'Developing Profile'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Overall Score</p>
            <p className="text-4xl font-bold text-primary">{profile.overall_score?.toFixed(1) || '--'}</p>
            <p className="text-xs text-muted-foreground">/ 10</p>
          </div>
        </div>

        {/* Evaluation Sources & Profile Completeness */}
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Private Coach:</span>
            {profile.evaluation_sources?.private_coach ? (
              <span className="flex items-center gap-1 text-emerald-400 text-sm">
                <Check size={14} /> Evaluated
              </span>
            ) : (
              <span className="text-muted-foreground text-sm flex items-center gap-1">
                <AlertCircle size={14} /> Not evaluated
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Team Coach:</span>
            {profile.evaluation_sources?.team_coach ? (
              <span className="flex items-center gap-1 text-emerald-400 text-sm">
                <Check size={14} /> Evaluated
              </span>
            ) : (
              <span className="text-muted-foreground text-sm flex items-center gap-1">
                <AlertCircle size={14} /> Not evaluated
              </span>
            )}
          </div>
          {/* V1: Performance Coach source — only shown if present so legacy
              profiles without performance evals don't get a phantom row. */}
          {hasPerformance && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Performance Coach:</span>
              <span className="flex items-center gap-1 text-orange-300 text-sm">
                <Activity size={14} /> Evaluated
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">Profile Completeness:</span>
            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${profile.profile_completeness || 0}%` }}
              />
            </div>
            <span className="text-sm font-medium">{profile.profile_completeness || 0}%</span>
          </div>
        </div>
      </div>

      {/* V1: Profile View Selector — filters radar & axis scores to a single
          coach perspective. Only renders when more than one source exists. */}
      {availableContexts.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-3 flex flex-wrap gap-2" data-testid="profile-view-selector">
          {availableContexts.map((ctx) => {
            const Icon = ctx.icon;
            const active = viewContext === ctx.id;
            return (
              <button
                key={ctx.id}
                onClick={() => setViewContext(ctx.id)}
                data-testid={`profile-view-${ctx.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs uppercase tracking-wider transition-colors ${
                  active
                    ? ctx.id === 'private_performance'
                      ? 'bg-orange-400/15 text-orange-300 border border-orange-400/40'
                      : 'bg-primary/15 text-primary border border-primary/40'
                    : 'bg-transparent text-muted-foreground border border-border/40 hover:border-border'
                }`}
              >
                <Icon size={14} />
                {ctx.label}
              </button>
            );
          })}
        </div>
      )}

      {/* V1: Performance Coach invite CTA — surfaces only when no Performance
          Coach has evaluated yet. Hides itself once one is connected. */}
      {!hasPerformance && (
        <div className="bg-orange-400/5 border border-orange-400/30 rounded-lg p-4 flex items-center gap-4" data-testid="invite-performance-cta">
          <div className="w-10 h-10 rounded-md bg-orange-400/15 flex items-center justify-center shrink-0">
            <Activity className="text-orange-300" size={20} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Add a Private Performance Coach</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Specialized physical evaluation (speed, agility, explosiveness, endurance). Adds depth to the unified profile.
            </p>
          </div>
          <button
            onClick={() => setShowPerfInvite(true)}
            className="text-xs uppercase tracking-wider px-3 py-2 rounded-md bg-orange-400/20 text-orange-300 hover:bg-orange-400/30 border border-orange-400/40"
            data-testid="invite-performance-btn"
          >
            Invite
          </button>
        </div>
      )}

      {/* Modal: Invite Performance Coach */}
      {showPerfInvite && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="invite-performance-modal">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
            <h3 className="font-heading text-lg mb-1 flex items-center gap-2">
              <Activity className="text-orange-300" size={18} /> Invite Performance Coach
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Enter the email of a registered Performance Coach. They will gain restricted access to evaluate only this athlete's physical attributes.
            </p>
            <form onSubmit={handleInvitePerformanceCoach} className="space-y-3">
              <input
                type="email"
                required
                value={perfInviteEmail}
                onChange={(e) => setPerfInviteEmail(e.target.value)}
                placeholder="coach@example.com"
                className="w-full bg-zinc-900/60 border border-border/40 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60"
                data-testid="invite-performance-email-input"
              />
              {perfInviteError && (
                <p className="text-xs text-red-400" data-testid="invite-performance-error">{perfInviteError}</p>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => { setShowPerfInvite(false); setPerfInviteError(null); }}
                  className="text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-border/40 text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={perfInviteSubmitting || !perfInviteEmail.includes('@')}
                  className="text-xs uppercase tracking-wider px-4 py-2 rounded-md bg-orange-400/30 text-orange-200 hover:bg-orange-400/40 border border-orange-400/40 disabled:opacity-40"
                  data-testid="invite-performance-submit"
                >
                  {perfInviteSubmitting ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Radar Chart Section */}
      {Object.keys(radarData).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="profile-radar-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">{radarTitle}</h3>
            {isPhysicalView && (
              <span className="text-[10px] uppercase tracking-widest text-orange-300 bg-orange-400/10 border border-orange-400/30 rounded-full px-2 py-0.5">
                Performance View
              </span>
            )}
          </div>
          <RadarChart data={radarData} size={320} />
        </div>
      )}

      {/* Development Timeline */}
      <DevelopmentTimeline history={timeline} loading={timelineLoading} />

      {/* Axis Scores */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg mb-4">
          {isPhysicalView ? 'Physical Axis Scores' : 'Axis Scores'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(filteredAxisScores).map(([skill, score]) => (
            <div key={skill} className="bg-muted/30 rounded-lg p-3">
              <p className="text-sm text-muted-foreground mb-1">{skill}</p>
              <p className="text-xl font-bold text-primary">{score?.toFixed(1)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Area Scores - Three Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Technical */}
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Target className="text-emerald-400" size={20} />
            </div>
            <h3 className="font-heading">Technical</h3>
          </div>
          <p className="text-3xl font-bold text-emerald-400 mb-1">
            {profile.area_scores?.technical?.toFixed(1) || '--'}
            <span className="text-lg text-muted-foreground font-normal"> / 10</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {((profile.area_scores?.technical || 0) / 30 * 100).toFixed(0)}% of total profile
          </p>
        </div>

        {/* Physical */}
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Zap className="text-blue-400" size={20} />
            </div>
            <h3 className="font-heading">Physical</h3>
          </div>
          <p className="text-3xl font-bold text-blue-400 mb-1">
            {profile.area_scores?.physical?.toFixed(1) || '--'}
            <span className="text-lg text-muted-foreground font-normal"> / 10</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {((profile.area_scores?.physical || 0) / 30 * 100).toFixed(0)}% of total profile
          </p>
        </div>

        {/* Tactical IQ */}
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Brain className="text-amber-400" size={20} />
            </div>
            <h3 className="font-heading">Tactical / IQ</h3>
          </div>
          <p className="text-3xl font-bold text-amber-400 mb-1">
            {profile.area_scores?.game_intelligence?.toFixed(1) || '--'}
            <span className="text-lg text-muted-foreground font-normal"> / 10</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {((profile.area_scores?.game_intelligence || 0) / 30 * 100).toFixed(0)}% of total profile
          </p>
        </div>
      </div>

      {/* AI Profile Summary (if available) */}
      {profile.ai_profile_summary ? (
        <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/30 rounded-lg p-6">
          <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
            <Star className="text-primary" size={20} />
            Profile Summary
            <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded">AI Generated</span>
          </h3>
          <p className="text-muted-foreground leading-relaxed">{profile.ai_profile_summary}</p>
          {profile.ai_player_type_reasoning && (
            <p className="text-xs text-muted-foreground mt-3 italic border-t border-border/30 pt-3">
              "{profile.ai_player_type_reasoning}"
            </p>
          )}
        </div>
      ) : (
        /* Fallback to deterministic insights */
        profile.insights?.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="font-heading text-lg mb-3">Profile Summary</h3>
            <p className="text-muted-foreground leading-relaxed">
              {profile.insights[0]}
            </p>
          </div>
        )
      )}

      {/* Top Strengths */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="text-emerald-400" size={20} />
          Top Strengths
          {profile.ai_generated && <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded ml-2">AI Enhanced</span>}
        </h3>
        {profile.strengths?.length > 0 ? (
          <div className="space-y-2">
            {profile.strengths.slice(0, 4).map((strength, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20"
              >
                <Check size={16} className="text-emerald-400" />
                <span>{strength}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No strengths identified yet. Keep training!</p>
        )}
      </div>

      {/* Development Priorities */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
          <Target className="text-amber-400" size={20} />
          Development Priorities
          {profile.ai_generated && <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded ml-2">AI Enhanced</span>}
        </h3>
        {profile.development_priorities?.length > 0 ? (
          <div className="space-y-2">
            {profile.development_priorities.slice(0, 3).map((priority, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20"
              >
                <AlertCircle size={16} className="text-amber-400" />
                <span>{priority}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No specific priorities identified.</p>
        )}
      </div>

      {/* AI Coach Insight Synthesis */}
      {profile.ai_coach_insight_synthesis && (
        <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/30 rounded-lg p-6">
          <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
            <Brain className="text-primary" size={20} />
            Coach Insight Analysis
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {profile.ai_coach_insight_synthesis}
          </p>
        </div>
      )}

      {/* Evaluation Reliability */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-lg mb-4">Evaluation Reliability</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <span className="text-muted-foreground">Private Coach Evaluation</span>
            {profile.evaluation_reliability?.private_coach ? (
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                profile.evaluation_reliability.private_coach === 'high' 
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : profile.evaluation_reliability.private_coach === 'medium'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {profile.evaluation_reliability.private_coach.charAt(0).toUpperCase() + 
                 profile.evaluation_reliability.private_coach.slice(1)}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">N/A</span>
            )}
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <span className="text-muted-foreground">Team Coach Evaluation</span>
            {profile.evaluation_reliability?.team_coach ? (
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                profile.evaluation_reliability.team_coach === 'high' 
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : profile.evaluation_reliability.team_coach === 'medium'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {profile.evaluation_reliability.team_coach.charAt(0).toUpperCase() + 
                 profile.evaluation_reliability.team_coach.slice(1)}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm">N/A</span>
            )}
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <p className="text-xs text-muted-foreground text-center">
        Profile last updated: {profile.last_updated 
          ? new Date(profile.last_updated).toLocaleDateString() 
          : 'N/A'}
      </p>
    </div>
  );
};

export default DevelopmentProfile;
